import React, { useLayoutEffect, useState } from "react";
import TrafficLights from "./TrafficLights";
import WindowFrame from "./WindowFrame";
import ShellLines from "./ShellLines";
import useGenie from "../hooks/useGenie";
import useShell from "../hooks/useShell";
import { USER, cwdLabel, folderLabel, inProject } from "../utils/shell";
import { BranchGlyph, CheckGlyph, CrossGlyph, FolderGlyph, GitHubGlyph } from "./promptGlyphs";
import homeIcon from "../assets/icons/ghostty/home.png";
import folderIcon from "../assets/icons/ghostty/folder.png";

// Ghostty's own font, loaded the first time the window opens.
const FONT = "https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;700&display=swap";
const loadFont = () => {
  if (document.getElementById("ghostty-font")) return;
  const link = document.createElement("link");
  link.id = "ghostty-font";
  link.rel = "stylesheet";
  link.href = FONT;
  document.head.appendChild(link);
};

// Default frame: up to 760 x 480, low and to the right, so a corner of it shows beside Notes.
const ghosttyFrame = (ws) => {
  const w = Math.min(760, ws.w - 80);
  const h = Math.min(480, ws.h - 40);
  return { x: (ws.w - w) / 2 + 220, y: (ws.h - h) / 2 + 140, w, h };
};

// Sylvester's oh-my-posh prompt (its default theme): his name, the folder, the branch when the
// folder is in a repository (this site's project is the one here, on master and up to date), and
// whether the last command worked.
const Prompt = ({ cwd, status }) => {
  const git = inProject(cwd);
  return (
    <span className="gh-prompt">
      <span className="gh-seg gh-seg--user">{` ${USER} `}</span>
      <span className={`gh-seg gh-seg--path ${git ? "is-before-git" : ""}`}>
        {" "}
        <span className="gh-glyph gh-glyph--folder">
          <FolderGlyph />
        </span>
        {` ${folderLabel(cwd)} `}
      </span>
      {git && (
        <span className="gh-seg gh-seg--git">
          {" "}
          <span className="gh-glyph gh-glyph--github">
            <GitHubGlyph />
          </span>{" "}
          <span className="gh-glyph gh-glyph--branch">
            <BranchGlyph />
          </span>
          {"master ≡ "}
        </span>
      )}
      <span className={`gh-seg gh-seg--status ${status ? "is-failed" : ""}`}>
        {" "}
        <span className={`gh-glyph ${status ? "gh-glyph--cross" : "gh-glyph--check"}`}>
          {status ? <CrossGlyph /> : <CheckGlyph />}
        </span>{" "}
      </span>
    </span>
  );
};

// Ghostty, like Sylvester's: the default dark theme, JetBrains Mono, and his prompt. A small zsh
// runs in it (hooks/useShell.js) with his resume in the home folder.
const GhosttyWindow = ({ workspace, open, front, onFocus, onClose }) => {
  const [exit, setExit] = useState("closed");
  const genie = useGenie({ app: "Ghostty", open, exit });
  const [maximized, setMaximized] = useState(false);

  // In front, it takes the keyboard (once it's shown: coming back from the Dock, the window is
  // hidden until the genie ends); behind another window, it lets go.
  const shell = useShell({ open, active: open && front && !genie.hidden, onExit: () => hide("closed") });

  useLayoutEffect(() => {
    if (open) loadFont();
  }, [open]);

  const hide = (how) => {
    setExit(how);
    if (how === "closed") {
      setMaximized(false);
      shell.reset();
    }
    onClose();
  };

  const cwd = shell.session ? shell.session.cwd : [];
  const state = open ? "nw--open" : `nw--${exit}`;
  return (
    <WindowFrame
      workspace={workspace}
      initial={ghosttyFrame}
      min={{ w: 360, h: 200 }}
      titlebar={31}
      open={open}
      front={front}
      zoomed={maximized}
      onFocus={onFocus}
      onToggleZoom={() => setMaximized((m) => !m)}
    >
      <div
        role="dialog"
        aria-label="Ghostty"
        aria-hidden={!open}
        ref={genie.ref}
        className={`nw gh ${state} ${maximized ? "nw--max" : ""} ${front ? "is-front" : ""} ${
          genie.hidden ? "is-genie-hidden" : ""
        }`}
      >
        <div className="traffic">
          <TrafficLights
            onClose={() => hide("closed")}
            onMinimize={() => genie.minimize(() => hide("minimized"))}
            onPrepare={genie.prepare}
            onZoom={() => setMaximized(!maximized)}
            zoomed={maximized}
          />
        </div>
        <div className="gh-title no-select">
          <img src={cwd.length ? folderIcon : homeIcon} alt="" draggable={false} />
          {cwdLabel(cwd)}
        </div>

        <div
          className="gh-screen edit-cursor"
          ref={shell.screenRef}
          onMouseUp={shell.onMouseUp}
          onScroll={shell.onScroll}
        >
          <ShellLines shell={shell} Prompt={Prompt} prefix="gh" tty="ttys013" />
        </div>
      </div>
    </WindowFrame>
  );
};

export default GhosttyWindow;
